/**
 * The concurrent-send refusal, and the copy that surfaces it (B-1731).
 *
 * Two brokers holding the same deal thread can send near-simultaneously. That is already handled:
 * the paired claim admission refuses the second POST with a 409 ("Admission refused" in
 * `chat.controller.ts`), so turns can never interleave. Only the read was wrong. The AI SDK's
 * transport collapses every non-OK response into `new Error(await response.text())` — the status is
 * thrown away and the raw JSON body becomes the message — so the chat shell rendered a correct,
 * expected "this thread already has a turn in flight" through the generic generation-error
 * disclosure, i.e. as a failure of Alby's.
 */

/**
 * Kept short: `ChatErrorDisclosure` truncates its title to one line (the full string stays in the
 * tooltip), so the actionable half lives in {@link CHAT_SEND_REFUSED_BUSY_DETAIL}.
 */
export const CHAT_SEND_REFUSED_BUSY_TITLE = "Alby is busy on this thread.";

/**
 * Deliberately does not say *who* holds the turn. The same 409 covers another broker's send, the
 * viewer's own still-active turn (a session stalled in `AWAITING_CHILD_PLAYBOOK` keeps refusing
 * until the cron sweeps it — B-1162), and a trigger-dispatched run.
 */
export const CHAT_SEND_REFUSED_BUSY_DETAIL =
  "A reply is already in progress on this thread. Wait for it to finish, then send your message again.";

export class ChatSendRefusedBusyError extends Error {
  constructor() {
    super(CHAT_SEND_REFUSED_BUSY_TITLE);
    this.name = "ChatSendRefusedBusyError";
  }
}

/**
 * Called from `useMcpChat`'s transport `fetch` on every response, while the status is still readable.
 * Throwing propagates out of `sendMessages`, so the SDK stores this error verbatim as the chat's
 * `error` instead of minting its own from the response body.
 *
 * Scoped to POST because that is the send; the GET reconnect resumes an already-admitted stream and
 * has no admission step to refuse. Narrowed to the two fields it reads so tests can pass literals —
 * jsdom has no runtime `Response` to build.
 */
export const throwIfSendWasRefused = (
  request: Pick<RequestInit, "method"> | undefined,
  response: Pick<Response, "status">
): void => {
  if (request?.method === "POST" && response.status === 409) {
    throw new ChatSendRefusedBusyError();
  }
};
